import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useMicrosoftAuth } from './useMicrosoftAuth';
import { useAccessCheck } from './useAdmin';
import { isSuperRole } from '@/lib/roles';

/**
 * Hook for checking user permissions
 */
export function usePermissions() {
  const { user, profile } = useAuth();
  const { user: msUser } = useMicrosoftAuth();

  // Same email derivation and session gate AppLayout uses, so this resolves to
  // the SAME React Query key — the admin answer is served from the cache of the
  // access check the app already ran, not a second round-trip.
  const currentEmail = user?.email || msUser?.email || '';
  const { data: access } = useAccessCheck(currentEmail, !!user);

  /**
   * Whether the current session is an admin. Used for the Admin Center route
   * guard, the audit log, and the OM admin controls.
   *
   * The answer comes from the SERVER — check_flow_access().is_admin, which is
   * is_allowed_admin() over allowed_users.role (migration 181) — so the UI can
   * no longer disagree with the RLS policies that gate the actual writes. It
   * previously came from a hardcoded email list that had drifted from the
   * roster, which showed one account admin controls that RLS then rejected.
   *
   * Undefined while the access check is in flight reads as NOT admin: admin UI
   * appears only on a positive server answer, never optimistically.
   */
  const isAdminUser = useMemo(() => {
    // A super admin on profiles.role stays an independent path — a different
    // roster with different semantics, intentionally not collapsed into the
    // allowlist. isSuperRole accepts BOTH spellings the column holds; the bare
    // 'super' this used to compare against exists in no row.
    if (isSuperRole(profile?.role)) return true;
    return access?.isAdmin === true;
  }, [profile, access]);

  /**
   * Whether `isAdminUser` reflects a definitive answer yet.
   *
   * Callers that REDIRECT on non-admin (the Admin Center route guard) must wait
   * for this. `isAdminUser` is false while the access check is in flight, so
   * acting on it immediately would bounce a genuine admin out of the page they
   * just opened. Callers that merely SHOW or HIDE a control can ignore it —
   * hiding until the answer lands is the correct, safe default.
   */
  const isAdminResolved = isSuperRole(profile?.role) || access !== undefined;

  return {
    role: profile?.role,
    isAdminUser,
    isAdminResolved,
  };
}
